import { Socket } from "socket.io-client";
import { Engine, SocketIoMessage } from "@/models/socketIo/socketIo";
import { DisconnectDescription } from "socket.io-client/build/esm-debug/socket";
import { addLocalActionHistory } from "./localActionHistory";
import DisconnectReason = Socket.DisconnectReason;
import { SamsScope } from "@/models/samsVbl";

const upgrade = (engine: Engine) =>
  new Promise((resolve, reject) => {
    engine.on("upgrade", (t) => {
      if (t.name === "websocket") {
        resolve(null);
      } else {
        reject(
          `Upgrade to websocket failed. Current transport type is ${t.name}`,
        );
      }
    });
  });

export const setUpSocketIo: (params: {
  socketIo: Socket;
  samsId: string;
  samsScope: SamsScope;
  onData: (data: SocketIoMessage) => void;
  onHeartbeat: () => void;
  onPing: () => void;
  onHandshake: (pingInterval: number) => void;
  onConnect: () => void;
  onConnectError: (e: Error) => void;
  onDisconnect: (r: DisconnectReason, e?: DisconnectDescription) => void;
}) => Promise<void> = async ({
  socketIo,
  samsId,
  samsScope,
  onData,
  onHeartbeat,
  onPing,
  onHandshake,
  onConnect,
  onConnectError,
  onDisconnect,
}) => {
  console.debug("setting up socketIo");

  socketIo.removeAllListeners("connect");
  socketIo.removeAllListeners("connect_error");
  socketIo.removeAllListeners("disconnect");

  socketIo.io.engine.on("handshake", (h) => {
    onHandshake(h.pingInterval);
  });

  socketIo.on("connect", async () => {
    const engine = socketIo.io.engine;
    engine.on("heartbeat", onHeartbeat);
    engine.on("ping", onPing);
    engine.on("message", (data: string) => {
      const parsedData = JSON.parse(
        data.substring(data.indexOf("[")),
      ) as SocketIoMessage;

      switch (parsedData[0]) {
        case "[SERVER] PULL ACTION HISTORY RESPONSE":
          console.warn("Unexpected PULL ACTION HISTORY RESPONSE via Socket.IO");
          return;
        case "[SERVER] SYNC RESPONSE":
          console.warn("Unexpected SYNC RESPONSE via Socket.IO");
          return;
        case "[SERVER] BROADCAST ACTION":
          addLocalActionHistory(samsId, parsedData[1]);
          break;
      }

      onData(parsedData);
    });

    engine.on("close", (reason) => {
      console.debug("engine closed", { reason });
    });

    // Emit the initial join room event
    socketIo.emit(
      "[CLIENT] ROOM JOIN",
      {
        matchId: samsId,
        auth:
          samsScope === "vbl"
            ? {
                match_uuid: samsId,
              }
            : null,
      },
      (response: unknown) => {
        console.debug("Server acknowledged the join:", response);
      },
    );

    if (engine.transport.name === "polling") {
      await upgrade(engine);
    }

    onConnect();
  });

  socketIo.on("connect_error", onConnectError);
  socketIo.on("disconnect", onDisconnect);
};
